import React from 'react';
import {
  ContentfulCategoryLandingPage,
  ContentfulContentHubHomepage,
  ContentfulGenericPage,
  ContentfulListingBlock,
  ContentfulListingEntry,
  ContentfulSubCategoryPage,
} from '@jbhi-fi/apis/src/contentful/types';
import { ListingBlockProps } from '../../ListingBlock';
import { ImageGrid } from '../../ImageGrid/ImageGrid';
import { ImageBoxCarousel } from '../../ImageBox/ImageBox';
import { ContentfulImage } from '../../ContentfulImage/ContentfulImage';
import { ThumbnailIconCarousel } from '../../ThumbnailIconCarousel/ThumbnailIconCarousel';
import { ThumbnailImageCarousel } from '../../ThumbnailImageCarousel/ThumbnailImageCarousel';
import { FeatureCardGrid } from '../../FeatureCardGrid/FeatureCardGrid';
import { IconName } from '@jbhi-fi/lanyard-ui/components/Icon';
import { AssetTransformer } from './AssetTransformer';
import { ContentBlockProps } from '../../ContentBlock/ContentBlock';
import { ThemeTransformer } from './ThemeTransformer';
import { useTrackingContext } from '../../Tracking';
import { breakpointSizePx } from '@jbhi-fi/lanyard-ui/themes';
import { ButtonTransformer } from './ButtonTransformer';
import { CollectionTransformer } from './CollectionTransformer';

export type ToListingBlockBaseOptions = {
  testId?: string;
};
export abstract class ListingBlockTransformer {
  static toContentBlockProps = (entry: ContentfulListingBlock): Partial<ContentBlockProps> => {
    const themePalette = ThemeTransformer.toContentThemePalette(entry.fields?.theme);

    return {
      style: { backgroundColor: themePalette.background },
    };
  };

  static toArticleRichTextLayoutBlockProps = (
    entry: ContentfulListingBlock
  ): {
    paddingTop?: number;
    backgroundColor?: React.CSSProperties['backgroundColor'];
  } => {
    const themePalette = ThemeTransformer.toContentThemePalette(entry.fields?.theme);

    return { paddingTop: 15, backgroundColor: themePalette.background ?? '#fff' };
  };

  static toListingBlockProps = (
    entry: ContentfulListingBlock,
    options?: ToListingBlockBaseOptions
  ): ListingBlockProps => {
    switch (entry.fields?.type?.toLowerCase()) {
      case 'image grid':
        return this.toListingBlockImageGridProps(entry, options);
      case 'icons':
        return this.toListingBlockThumbnailIconCarouselProps(entry, options);
      case 'image cards':
        return this.toListingBlockFeatureCardGridProps(entry, options);
      case 'image boxes':
        return this.toListingBlockImageBoxCarouselProps(entry, options);
      case 'image thumbnails':
      case 'image thumbnails (double)':
        return this.toListingBlockThumbnailImageCarouselProps(entry, options);
      default:
        return {
          ...this._toBaseListingBlockProps(entry, options),
          children: <></>,
        };
    }
  };

  private static _toBaseListingBlockProps = (
    entry: ContentfulListingBlock,
    options?: ToListingBlockBaseOptions
  ): Omit<ListingBlockProps, 'children'> => {
    return {
      heading: entry?.fields?.heading,
      palette: entry?.fields?.theme?.fields ? ThemeTransformer.toContentThemePalette(entry.fields.theme) : undefined,
      subHeading: entry?.fields?.subText,
      linkProps: entry?.fields?.ctaButton?.fields
        ? {
            label: entry.fields.ctaButton.fields.label,
            href: entry.fields.ctaButton.fields.url,
            shouldOpenInNewWindow: entry.fields.ctaButton.fields.shouldOpenInNewWindow,
          }
        : undefined,
      testId: options?.testId,
    };
  };

  static toListingBlockImageGridProps = (
    entry: ContentfulListingBlock,
    options?: ToListingBlockBaseOptions
  ): ListingBlockProps => {
    return {
      ...this._toBaseListingBlockProps(entry, options),
      children: (
        <ImageGrid
          images={entry.fields?.entries?.filter(Boolean)?.map((entry, i) => (
            <ImageGridImageComponent entry={entry} key={i} />
          ))}
        />
      ),
    };
  };

  static toListingBlockImageBoxCarouselProps = (
    entry: ContentfulListingBlock,
    options?: ToListingBlockBaseOptions
  ): ListingBlockProps => {
    return {
      ...this._toBaseListingBlockProps(entry, options),
      children: (
        <ImageBoxCarousel
          imageBoxes={
            entry.fields?.entries?.filter(Boolean).map(entry => {
              return {
                ...(AssetTransformer.toImageProps(entry.fields?.image) ?? { src: '', alt: '' }),
                href: this.listingEntryToHref(entry),
              };
            }) ?? []
          }
        />
      ),
    };
  };

  static toListingBlockFeatureCardGridProps = (
    entry: ContentfulListingBlock,
    options?: ToListingBlockBaseOptions
  ): ListingBlockProps => {
    return {
      ...this._toBaseListingBlockProps(entry, options),
      children: (
        <FeatureCardGrid
          cards={
            entry.fields?.entries?.filter(Boolean)?.map(entry => ({
              title: entry.fields?.heading ?? '',
              href: this.listingEntryToHref(entry),
              image: (
                <ContentfulImage
                  {...(AssetTransformer.toContentfulImageProps(entry.fields?.image) ?? {})}
                  sizes={`(max-width: ${breakpointSizePx.md}) 200px, 350px`}
                />
              ),
            })) ?? []
          }
        />
      ),
    };
  };

  static toListingBlockThumbnailIconCarouselProps = (
    entry: ContentfulListingBlock,
    options?: ToListingBlockBaseOptions
  ): ListingBlockProps => {
    const { fields } = entry;
    return {
      children: (
        <ThumbnailIconCarousel
          title={fields?.heading ?? ''}
          palette={fields?.theme?.fields ? ThemeTransformer.toContentThemePalette(fields.theme) : undefined}
          linkProps={fields?.ctaButton?.fields ? ButtonTransformer.toGoToLinkProps(fields.ctaButton) : undefined}
          thumbnails={
            fields?.entries
              ?.filter(entry => entry?.fields)
              ?.map(entry => ({
                title: entry.fields?.heading ?? '',
                href: this.listingEntryToHref(entry),
                iconName: entry.fields?.icon?.fields?.name as IconName,
              })) ?? []
          }
        />
      ),
      testId: options?.testId,
    };
  };

  static toListingBlockThumbnailImageCarouselProps = (
    entry: ContentfulListingBlock,
    options?: ToListingBlockBaseOptions
  ): ListingBlockProps => {
    const { fields } = entry;
    return {
      ...this._toBaseListingBlockProps(entry, options),
      children: (
        <ThumbnailImageCarousel
          isStacked={{ xs: true, md: fields?.type?.toLowerCase()?.includes('double') }}
          carouselProps={{ rewind: true }}
          thumbnails={
            fields?.entries
              ?.filter(entry => entry?.fields)
              ?.map(entry => ({
                href: this.listingEntryToHref(entry),
                title: entry.fields?.heading ?? '',
                isFullBleedImage: true,
                image: (
                  <ContentfulImage
                    {...(AssetTransformer.toContentfulImageProps(entry.fields?.image) ?? {})}
                    sizes="120px"
                  />
                ),
              })) ?? []
          }
        />
      ),
    };
  };
  static listingEntryToHref = (entry: ContentfulListingEntry): string => {
    const isSubCategory = (
      entry: ContentfulSubCategoryPage | ContentfulCategoryLandingPage | ContentfulContentHubHomepage
    ): entry is ContentfulSubCategoryPage => entry?.sys?.contentType?.sys?.id === 'subLandingPage';

    const getPageHref = (entry: ContentfulGenericPage): string => {
      if (!entry?.fields?.urlSlug) return '';
      if (!entry?.fields?.parentPage?.fields?.urlSlug) {
        return `/blogs/${entry?.fields?.urlSlug}`;
      }
      return `/blogs/${
        isSubCategory(entry?.fields?.parentPage)
          ? entry?.fields?.parentPage?.fields?.categoryPage?.fields?.urlSlug
          : entry?.fields?.parentPage?.fields?.urlSlug
      }/${entry?.fields?.urlSlug}`;
    };

    if (entry?.fields?.url) return entry?.fields?.url;
    if (entry?.fields?.collection) return CollectionTransformer.toHref(entry?.fields?.collection);
    if (entry?.fields?.page?.fields?.urlSlug) return getPageHref(entry?.fields?.page);
    return '';
  };
}

const ImageGridImageComponent = ({ entry }: { entry: ContentfulListingEntry }) => {
  const { tracker } = useTrackingContext();
  return (
    <a
      href={ListingBlockTransformer.listingEntryToHref(entry)}
      onClick={() =>
        tracker?.trackClick({ label: entry.fields?.heading, extraData: { moduleName: 'Image grid image' } })
      }
    >
      <ContentfulImage {...AssetTransformer.toContentfulImageProps(entry.fields?.image)} sizes="240px" />
    </a>
  );
};
